"use client";

import { getDocumentOrThrow } from "@faire/web--source/common/globals/getDocument";
import { Column, IFlexProps } from "@faire/web--source/slate/Layout";
import { offset } from "@floating-ui/dom";
import { useFloating, Placement } from "@floating-ui/react";
import React, { useEffect } from "react";
import ReactDOM from "react-dom";

interface IProps {
  children?: React.ReactNode | undefined;
  tooltip: React.ReactNode;
  placement: Placement;
  tooltipZIndex?: number;
  className?: string;
  tooltipMainAxisOffset?: number;
  tooltipCrossAxisOffset?: number;
  onMouseOver?: () => void;
  "data-test-id"?: string;
  align?: IFlexProps["align"];
}

/**
 * This is a wrapper wraps web-ui's tooltip in the so it can be positioned
 * with floating-ui, and react portal to overcome some limitation with
 * position: absolute
 */
export const PortalTooltip: React.FC<IProps> = (props) => {
  const bodyRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    bodyRef.current = getDocumentOrThrow().body;
  }, []);

  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const { floatingStyles, refs } = useFloating({
    placement: props.placement,
    middleware: [
      offset({
        mainAxis: props.tooltipMainAxisOffset ?? 0,
        crossAxis: props.tooltipCrossAxisOffset ?? 0,
      }),
    ],
  });

  const handleMouseOver = () => {
    setIsOpen(true);
    props.onMouseOver?.();
  };

  const handleMouseOut = () => {
    setIsOpen(false);
  };

  return (
    <Column
      className={props.className}
      data-test-id={props["data-test-id"]}
      ref={refs.setReference}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseOut}
      justify="center"
      align={props.align ?? "center"}
    >
      {props.children}
      {isOpen && bodyRef.current
        ? ReactDOM.createPortal(
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex: props.tooltipZIndex ?? 5,
              }}
            >
              {props.tooltip}
            </div>,
            bodyRef.current
          )
        : null}
    </Column>
  );
};
